// apps/web/src/pages/painel/dashboard-page.tsx — Fase 17.8, Plano 04 (D-01, D-02, D-03, D-04, D-13).
// Home do /painel: uma aba de perfil por vez, só as permitidas pelo GATE_BLOCO da 16.1. A aba inicial é a última usada (cookie do
// usuário); no primeiro acesso, Admin abre em Diretoria e os demais na primeira permitida. A aba restaurada é a primeira a
// renderizar (enquanto a sessão carrega só há esqueleto). Todas as abas mostram a home nova (Plano 25, D-20: o fallback legado dos
// blocos da 16.1 saiu; os endpoints /painel/blocos/* da API continuam). O ao vivo (Plano 12): `useAoVivo` recarrega a home da aba ativa por evento do stream (debounce de
// 5 s) e a cada ~2 min com a aba visível; o 401 leva ao login e a hora da última atualização alimenta o indicador da topbar.
// Só a mudança vinda do stream rola e brilha (`mudancasEntre` + `deveBrilhar`, calma de 5 s por card); primeira carga, troca de aba,
// retorno ao primeiro plano e a recarga de 2 min trocam os valores em silêncio.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import type { PerfilHome } from '@pieltec/contracts';
import { CabecalhoHome } from '../../components/painel/home/cabecalho-home';
import { HomePerfil, type DadosAnteriores, type RespostaHome } from '../../components/painel/home/home-perfil';
import type { OrigemNumero } from '../../components/painel/home/numero-ao-vivo';
import { ID_PAINEL_DA_ABA, idDaAba, PerfilTabs } from '../../components/painel/home/perfil-tabs';
import { RegiaoCarregando, SemIndicadores } from '../../components/painel/home/regiao-estado';
import { useCanalAoVivo } from '../../components/shell/ao-vivo-provider';
import { useSidebar } from '../../components/ui/sidebar';
import { useAoVivo, type OrigemRecarga } from '../../hooks/use-ao-vivo';
import { gravarAbaPainel, lerAbaPainel } from '../../lib/prefs-ui';
import { abaInicial, abasPermitidas } from '../../lib/painel/home/abas';
import { deveBrilhar, DURACAO_BRILHO_MS, mudancasEntre } from '../../lib/painel/home/brilho';
import { carregarHome, type ResultadoHome } from '../../lib/painel/home/carga-home';
import { montarFrase, poEstaMedido } from '../../lib/painel/home/frase';
import { primeiroNome } from '../../lib/painel/home/saudacao';
import { obterSessaoAtual, type ObterSessaoResult } from '../../lib/session';
import '../../styles/painel-home.css';

/** Estado da home de uma aba: resultado atual (`null` = carregando), últimos dados bons e se é a primeira carga. */
interface EstadoHomeAba {
  readonly resultado: ResultadoHome<RespostaHome> | null;
  readonly anterior?: DadosAnteriores;
  readonly primeira: boolean;
  /** Origem dos dados atuais quando vieram de uma recarga em segundo plano (`undefined` = carga da própria página). */
  readonly origem?: OrigemNumero;
  /** Cards que brilham agora (só em recarga ao vivo), limpos depois de ~1 s. */
  readonly brilhos: readonly string[];
}

type EstadosDasHomes = Readonly<Partial<Record<PerfilHome, EstadoHomeAba>>>;

/** Carregador da home de cada perfil. */
const CARREGADORES_HOME: Readonly<Record<PerfilHome, () => Promise<ResultadoHome<RespostaHome>>>> = {
  pcp: () => carregarHome('pcp'),
  qualidade: () => carregarHome('qualidade'),
  almoxarifado: () => carregarHome('almoxarifado'),
  compras: () => carregarHome('compras'),
  faturamento: () => carregarHome('faturamento'),
  diretoria: () => carregarHome('diretoria'),
};

/** Quem pediu a carga: a própria página (primeira carga e "Tentar novamente") ou o `useAoVivo` (evento do stream, timer, retorno da aba). */
type OrigemCarga = 'inicial' | OrigemRecarga;

const TITULO_DA_PAGINA = 'Painel · Pieltec';

export function DashboardPage(): ReactElement {
  const { isMobile, setOpen, setOpenMobile } = useSidebar();
  const [sessao, setSessao] = useState<ObterSessaoResult | null>(null);
  const [escolhida, setEscolhida] = useState<PerfilHome | null>(null);
  const [homes, setHomes] = useState<EstadosDasHomes>({});
  const [agora] = useState(() => new Date());
  const { registrarAtualizacao } = useCanalAoVivo();
  // Cargas já disparadas por aba (home, bloco): trocar de aba não recarrega o que já foi carregado.
  const iniciadas = useRef<Set<string>>(new Set());
  // Espelho do estado das homes (para comparar a resposta nova com a anterior fora do `setState`) e calma do brilho por card.
  const homesRef = useRef<EstadosDasHomes>({});
  const ultimoBrilho = useRef<Map<string, number>>(new Map());
  const temporizadoresBrilho = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const permissoes = useMemo<readonly string[]>(() => (sessao?.autenticado ? sessao.usuario.permissions : []), [sessao]);
  const userId = sessao?.autenticado ? sessao.usuario.userId : '';
  const abas = useMemo(() => abasPermitidas(permissoes), [permissoes]);

  // Aba ativa: a escolhida nesta sessão (se ainda permitida) ou a inicial (cookie validado, Admin em Diretoria, primeira permitida).
  const ativa = useMemo<PerfilHome | null>(() => {
    if (!sessao?.autenticado) return null;
    if (escolhida !== null && abas.includes(escolhida)) return escolhida;
    const cookie = typeof document === 'undefined' ? '' : document.cookie;
    return abaInicial({ permitidas: abas, permissoes, salva: lerAbaPainel(cookie, userId, abas) });
  }, [sessao, escolhida, abas, permissoes, userId]);

  // Home de uma aba: mantém os últimos dados bons enquanto recarrega e quando a recarga falha (U-26). Recarga em segundo
  // plano (ao vivo, timer) não pisca o esqueleto e, se falhar, deixa a tela como está (o indicador mostra a idade do dado).
  const carregarHomeDaAba = useCallback(async (perfil: PerfilHome, origem: OrigemCarga = 'inicial'): Promise<ResultadoHome<RespostaHome>> => {
    const carregador = CARREGADORES_HOME[perfil];
    const segundoPlano = origem !== 'inicial';
    if (!segundoPlano) {
      setHomes((atual) => ({
        ...atual,
        [perfil]: { resultado: null, anterior: atual[perfil]?.anterior, primeira: atual[perfil]?.primeira ?? true, origem: atual[perfil]?.origem, brilhos: [] },
      }));
    }
    const resultado = await carregador();

    // Brilho (D-14): só em recarga ao vivo, só nos cards que mudaram e respeitando a calma de 5 s por card.
    let brilhos: readonly string[] = [];
    if (origem === 'ao-vivo' && resultado.tipo === 'sucesso') {
      const agoraMs = Date.now();
      brilhos = mudancasEntre(homesRef.current[perfil]?.anterior?.dados, resultado.dados).filter((card) =>
        deveBrilhar(card, 'ao-vivo', ultimoBrilho.current.get(`${perfil}:${card}`), agoraMs),
      );
      for (const card of brilhos) ultimoBrilho.current.set(`${perfil}:${card}`, agoraMs);
    }
    const origemDosDados: OrigemNumero | undefined = segundoPlano ? origem : undefined;

    setHomes((atual) => {
      const anterior = atual[perfil]?.anterior;
      if (segundoPlano && (resultado.tipo === 'erro' || resultado.tipo === 'nao-autenticado')) return atual;
      return {
        ...atual,
        [perfil]: {
          resultado,
          anterior: resultado.tipo === 'sucesso' ? { dados: resultado.dados, em: new Date() } : anterior,
          // A contagem do numeral roda só na primeira carga com sucesso; recargas depois disso são silenciosas.
          primeira: anterior === undefined,
          origem: origemDosDados,
          brilhos,
        },
      };
    });
    if (brilhos.length > 0) {
      const temporizador = setTimeout(() => {
        temporizadoresBrilho.current.delete(temporizador);
        setHomes((atual) => {
          const estado = atual[perfil];
          return estado === undefined || estado.brilhos !== brilhos ? atual : { ...atual, [perfil]: { ...estado, brilhos: [] } };
        });
      }, DURACAO_BRILHO_MS);
      temporizadoresBrilho.current.add(temporizador);
    }
    return resultado;
  }, []);

  // Perfil da aba ativa (a home recarrega sozinha por evento do stream, timer e retorno ao primeiro plano).
  const recarregarAba = useCallback(
    (origem: OrigemRecarga): Promise<ResultadoHome<unknown>> =>
      ativa === null ? Promise.resolve({ tipo: 'erro' }) : carregarHomeDaAba(ativa, origem),
    [ativa, carregarHomeDaAba],
  );
  useAoVivo({ perfilAtivo: ativa, recarregar: recarregarAba });

  // Indicador da topbar: a hora do último dado bom da aba ativa (some ao sair da página).
  const atualizadaEm = ativa === null ? null : (homes[ativa]?.anterior?.em ?? null);
  useEffect(() => {
    registrarAtualizacao(atualizadaEm);
  }, [atualizadaEm, registrarAtualizacao]);
  useEffect(() => () => registrarAtualizacao(null), [registrarAtualizacao]);

  useEffect(() => {
    homesRef.current = homes;
  }, [homes]);
  useEffect(() => {
    const temporizadores = temporizadoresBrilho.current;
    return () => temporizadores.forEach((temporizador) => clearTimeout(temporizador));
  }, []);

  useEffect(() => {
    document.title = TITULO_DA_PAGINA;
  }, []);

  useEffect(() => {
    let ativo = true;
    void obterSessaoAtual().then((resultado) => {
      if (ativo) setSessao(resultado);
    });
    return () => {
      ativo = false;
    };
  }, []);

  // Carrega ao ativar a aba (uma vez por aba); depois disso, só "Tentar novamente".
  useEffect(() => {
    if (ativa === null) return;
    const iniciar = (chave: string, acao: () => void): void => {
      if (iniciadas.current.has(chave)) return;
      iniciadas.current.add(chave);
      acao();
    };
    iniciar(`home:${ativa}`, () => void carregarHomeDaAba(ativa));
  }, [ativa, carregarHomeDaAba]);

  const abrirMenu = useCallback((): void => {
    if (isMobile) setOpenMobile(true);
    else setOpen(true);
  }, [isMobile, setOpen, setOpenMobile]);

  const trocarAba = useCallback(
    (perfil: PerfilHome): void => {
      setEscolhida(perfil);
      if (userId !== '') gravarAbaPainel(userId, perfil);
    },
    [userId],
  );

  // Sessão ainda carregando: só esqueleto. Nenhuma aba padrão aparece antes da restaurada (D-02).
  if (sessao === null) {
    return (
      <section className="painel-home" data-testid="painel-home" aria-busy="true">
        <RegiaoCarregando id="pagina" altura={320} />
      </section>
    );
  }
  if (!sessao.autenticado) return <section className="painel-home" data-testid="painel-home" />;

  const nome = primeiroNome(sessao.usuario.displayName, sessao.usuario.username);

  // Sessão sem nenhuma aba (destinoInicial a deixa em /painel): aviso em vez de página em branco.
  if (ativa === null) {
    return (
      <section className="painel-home" data-testid="painel-home">
        <CabecalhoHome perfil="diretoria" nome={nome} agora={agora} />
        <div className="painel-legado">
          <SemIndicadores permissoes={permissoes} onAbrirMenu={abrirMenu} />
        </div>
      </section>
    );
  }

  const temSeletor = abas.length > 1;
  const estadoHome = homes[ativa];
  // Frase de status (D-10): usa os fatos da home carregada (ou os últimos dados bons durante uma recarga); sem dados, a frase calma.
  const dadosDaHome = estadoHome?.resultado?.tipo === 'sucesso' ? estadoHome.resultado.dados : estadoHome?.anterior?.dados;
  const fatos = dadosDaHome?.perfil === ativa ? dadosDaHome.fatos : undefined;

  return (
    <section className="painel-home" data-perfil={ativa} data-testid="painel-home">
      <PerfilTabs abas={abas} ativa={ativa} onTrocar={trocarAba} />
      <CabecalhoHome perfil={ativa} nome={nome} agora={agora} frase={montarFrase(ativa, fatos, undefined, { poMedido: poEstaMedido(dadosDaHome?.perfil === 'almoxarifado' ? dadosDaHome.assinatura : undefined) })} />
      <div
        key={ativa}
        role={temSeletor ? 'tabpanel' : undefined}
        id={temSeletor ? ID_PAINEL_DA_ABA : undefined}
        aria-labelledby={temSeletor ? idDaAba(ativa) : undefined}
        className="min-w-0"
      >
        <HomePerfil
          perfil={ativa}
          resultado={estadoHome?.resultado ?? null}
          anterior={estadoHome?.anterior}
          permissoes={permissoes}
          primeiraCarga={estadoHome?.primeira ?? true}
          origem={estadoHome?.origem}
          brilhos={estadoHome?.brilhos}
          onTentarNovamente={() => void carregarHomeDaAba(ativa)}
        />
      </div>
    </section>
  );
}
